import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { type RecipeComponent } from './recipeService';

export interface Item {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  flavors?: string[];
  newItem: boolean;
  available: boolean;
  createdAt: string;
  updatedAt: string;
  imagePath?: string;
  imageUrl?: string;
  recipeId?: string;
  recipeComponents?: RecipeComponent[];
}

export interface CreateItemRequest {
  name: string;
  description: string;
  price: number;
  category: string;
  flavors?: string[];
  newItem?: boolean;
  available?: boolean;
  recipeId?: string;
  recipeComponents?: RecipeComponent[];
}

export interface UpdateItemRequest {
  name?: string;
  description?: string;
  price?: number;
  category?: string;
  flavors?: string[];
  newItem?: boolean;
  available?: boolean;
  recipeId?: string | null;
  recipeComponents?: RecipeComponent[];
}

class ItemService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async listItems(cnpj?: string): Promise<Item[]> {
    try {
      const response: AxiosResponse<Item[]> = await this.client.get('/admin/items', {
        params: cnpj ? { cnpj } : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar itens');
    }
  }

  async createItem(data: CreateItemRequest): Promise<Item> {
    try {
      const response: AxiosResponse<Item> = await this.client.post('/admin/items', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar item');
    }
  }

  async updateItem(id: string, data: UpdateItemRequest): Promise<Item> {
    try {
      const response: AxiosResponse<Item> = await this.client.put(`/admin/items/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar item');
    }
  }

  async uploadItemImage(id: string, file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<{ imagePath: string }> = await this.client.post(
        `/admin/items/${id}/image`,
        formData,
        {
          headers: {
            // Deixar o browser definir o boundary corretamente
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data.imagePath;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao enviar imagem do item');
    }
  }

  async deleteItem(id: string, cnpj?: string): Promise<void> {
    try {
      await this.client.delete(`/admin/items/${id}`, {
        params: cnpj ? { cnpj } : undefined,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao deletar item');
    }
  }
}

export const itemService = new ItemService();
export default itemService;
